import type { DetectionBox } from "@aimosaic/core";
import { zipSync } from "fflate";
import type { OcrDiagnostics } from "./detection/ocr";
import { type PreparedImageFile, prepareImageFile } from "./fileInput";
import type { RenderedImage } from "./renderedImage";

export type BatchRunStatus = "pending" | "running" | "done" | "failed";

export interface BatchImage {
  url: string;
  dataUrl: string;
  name: string;
  width: number;
  height: number;
}

export interface BatchItem {
  id: string;
  fileName: string;
  image?: BatchImage;
  status: BatchRunStatus;
  boxes: DetectionBox[];
  diagnostics?: { finalCount?: number; ocr?: OcrDiagnostics };
  renderedImage?: RenderedImage;
  error?: string;
}

interface ToBatchItemsDeps {
  prepare?: (file: File) => Promise<PreparedImageFile>;
  readDimensions: (dataUrl: string) => Promise<{ width: number; height: number }>;
}

interface RunBatchDetectionOptions {
  rerunDone?: boolean;
}

export async function toBatchItems(files: Iterable<File> | FileList, deps: ToBatchItemsDeps): Promise<BatchItem[]> {
  const prepare = deps.prepare ?? prepareImageFile;
  const images = Array.from(files).filter((file) => file.type.startsWith("image/"));
  const items: BatchItem[] = [];

  for (const file of images) {
    const prepared = await prepare(file);
    const dimensions = await deps.readDimensions(prepared.dataUrl);
    revokeObjectUrl(prepared.objectUrl);
    items.push({
      id: createBatchId(file.name, items.length),
      fileName: prepared.name,
      image: {
        url: prepared.dataUrl,
        dataUrl: prepared.dataUrl,
        name: prepared.name,
        width: dimensions.width,
        height: dimensions.height
      },
      status: "pending",
      boxes: []
    });
  }

  return items;
}

export async function runBatchDetection(
  items: BatchItem[],
  detectItem: (item: BatchItem) => Promise<Pick<BatchItem, "boxes" | "diagnostics" | "renderedImage">>,
  onItem?: (item: BatchItem, index: number) => void,
  options: RunBatchDetectionOptions = {}
): Promise<BatchItem[]> {
  const next = [...items];
  for (let index = 0; index < next.length; index += 1) {
    const item = next[index];
    if (!item || (item.status === "done" && !options.rerunDone)) continue;
    const runnableItem = options.rerunDone
      ? { ...item, boxes: [], diagnostics: undefined, renderedImage: undefined, error: undefined }
      : item;
    next[index] = { ...runnableItem, status: "running", error: undefined };
    onItem?.(next[index], index);
    try {
      const result = await detectItem(next[index]);
      next[index] = {
        ...next[index],
        status: "done",
        boxes: result.boxes,
        diagnostics: result.diagnostics,
        renderedImage: result.renderedImage
      };
    } catch (error) {
      next[index] = {
        ...next[index],
        status: "failed",
        error: error instanceof Error ? error.message : "Batch detection failed"
      };
    }
    onItem?.(next[index], index);
  }
  return next;
}

export function createBatchZipDownloadName(now = new Date()): string {
  const year = now.getFullYear();
  const month = padDatePart(now.getMonth() + 1);
  const day = padDatePart(now.getDate());
  const hours = padDatePart(now.getHours());
  const minutes = padDatePart(now.getMinutes());
  const seconds = padDatePart(now.getSeconds());
  return `natgarim-batch-results-${year}${month}${day}-${hours}${minutes}${seconds}.zip`;
}

export async function exportBatchZip(items: BatchItem[]): Promise<Blob> {
  const used = new Map<string, number>();
  const entries: Record<string, Uint8Array> = {};

  for (const item of items) {
    if (item.status !== "done" || !item.renderedImage) continue;
    const filename = uniqueFilename(item.renderedImage.filename, used);
    entries[filename] = new Uint8Array(await item.renderedImage.blob.arrayBuffer());
  }

  const bytes = zipSync(entries);
  return new Blob([copyToArrayBuffer(bytes)], { type: "application/zip" });
}

function createBatchId(fileName: string, index: number): string {
  return `${Date.now().toString(36)}-${index}-${fileName.replace(/[^a-z0-9가-힣]+/gi, "-")}`;
}

function uniqueFilename(filename: string, used: Map<string, number>): string {
  const count = used.get(filename) ?? 0;
  used.set(filename, count + 1);
  if (count === 0) return filename;
  const dotIndex = filename.lastIndexOf(".");
  if (dotIndex <= 0) return `${filename}-${count + 1}`;
  return `${filename.slice(0, dotIndex)}-${count + 1}${filename.slice(dotIndex)}`;
}

function padDatePart(value: number): string {
  return String(value).padStart(2, "0");
}

function copyToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.length);
  copy.set(bytes);
  return copy.buffer;
}

function revokeObjectUrl(url: string): void {
  if (!url.startsWith("blob:") || typeof URL.revokeObjectURL !== "function") return;
  URL.revokeObjectURL(url);
}
